import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/auth/auth-context";
import { useGlobalNotifications } from "../../../../hooks/use-global-notifications";
import { normalizeAvatarUrl } from "../../../../utils/avatar-url";

interface ProfileData {
    first_name?: string;
    last_name?: string;
    nickname?: string;
    company_name?: string;
    title?: string;
    state?: string;
    avatar_url?: string;
    [key: string]: any;
}

// type PendingPatch = Partial<ProfileData>;

type PendingEntry = { value: any; gen: number };
type PendingMap = Record<string, PendingEntry>;

export const useProfile = () => {
    const { profile, profileLoading, updateProfile, uploadProfilePhoto } =
        useAuth();
    const { showError, showSuccess } = useGlobalNotifications();

    const [draft, setDraft] = useState<ProfileData>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ✅ sticky displayed avatar url (prevents flicker to initials on unrelated profile updates)
    const [displayAvatarUrl, setDisplayAvatarUrl] = useState<
        string | undefined
    >(undefined);

    // Debounce + diff tracking
    const debounceMs = 600;

    const timerRef = useRef<number | null>(null);

    // ✅ pending changes are stored with a generation number per-field
    const pendingRef = useRef<PendingMap>({});
    const lastSyncedRef = useRef<ProfileData>({});
    const inFlightRef = useRef(false);
    const flushRequestedRef = useRef(false);

    // ✅ keep the last known good avatar URL
    const lastGoodAvatarUrlRef = useRef<string | undefined>(undefined);

    // ✅ allow the UI to mark a specific URL as "bad" if the image load fails
    const badAvatarUrlRef = useRef<string | undefined>(undefined);

    // ✅ dirty tracking so rehydration never overwrites what user is editing
    const dirtyRef = useRef<Set<string>>(new Set());

    // ✅ per-field generations (prevents older server responses from "winning")
    const fieldGenRef = useRef<Record<string, number>>({});

    // ✅ keep latest draft in a ref so async closures never use stale state
    const draftRef = useRef<ProfileData>({});
    useEffect(() => {
        draftRef.current = draft || {};
    }, [draft]);

    const applyAvatarFromProfile = (p: ProfileData) => {
        const maybe = normalizeAvatarUrl((p as any)?.avatar_url);
        if (maybe) {
            lastGoodAvatarUrlRef.current = maybe;

            if (badAvatarUrlRef.current === maybe) {
                badAvatarUrlRef.current = undefined;
            }

            if (maybe !== badAvatarUrlRef.current) {
                setDisplayAvatarUrl(maybe);
            }
        } else {
            if (!displayAvatarUrl && lastGoodAvatarUrlRef.current) {
                const fallback = lastGoodAvatarUrlRef.current;
                if (fallback && fallback !== badAvatarUrlRef.current) {
                    setDisplayAvatarUrl(fallback);
                }
            }
        }
    };

    /**
     * ✅ CRITICAL FIX:
     * While the user is editing ANYTHING (dirty/pending/inFlight),
     * we do NOT hydrate text fields from context `profile`.
     *
     * This prevents "stale rehydrate" snapping other fields back mid-edit.
     */
    const isEditingNow = () => {
        const hasDirty = dirtyRef.current.size > 0;
        const hasPending = Object.keys(pendingRef.current || {}).length > 0;
        const inFlight = inFlightRef.current;
        return hasDirty || hasPending || inFlight;
    };

    // ✅ hydrate local draft from context profile (source of truth)
    useEffect(() => {
        const p = (profile ?? {}) as any as ProfileData;

        // Always update avatar cache (safe)
        applyAvatarFromProfile(p || {});

        const incoming = (p || {}) as ProfileData;

        // Always keep lastSynced as server truth (safe)
        lastSyncedRef.current = incoming || {};
        setError(null);

        // 🚫 If editing, DO NOT merge server -> draft (this is what fixes the snap)
        if (isEditingNow()) {
            return;
        }

        // ✅ Not editing: safe to hydrate everything
        setDraft((prev) => {
            const next: any = { ...(prev || {}) };
            for (const k of Object.keys(incoming || {})) {
                next[k] = (incoming as any)[k];
            }
            return next as ProfileData;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile]);

    const firstName = draft.first_name || "";
    const lastName = draft.last_name || "";
    const title = draft.title || "";
    const nickname = draft.nickname || "";
    const company = draft.company_name || "";
    const state = draft.state || "";

    const initials = useMemo(() => {
        const firstInitial = (firstName.charAt(0) || "").toUpperCase();
        const lastInitial = (lastName.charAt(0) || "").toUpperCase();
        const out = `${firstInitial}${lastInitial}`.trim();
        return out || (firstName.charAt(0) || "?").toUpperCase();
    }, [firstName, lastName]);

    const flushPending = async () => {
        if (inFlightRef.current) {
            flushRequestedRef.current = true;
            return;
        }
        flushRequestedRef.current = false;

        const pendingMap = pendingRef.current || {};
        const keys = Object.keys(pendingMap);

        if (!keys.length) return;

        // Build an update payload that only includes values that actually changed vs last synced
        const last = lastSyncedRef.current || {};
        const updateData: Record<string, any> = {};

        // snapshot field generations for THIS request
        const sentGenSnapshot: Record<string, number> = {};

        for (const k of keys) {
            const entry = pendingMap[k];
            const nextVal = entry?.value;
            const prevVal = (last as any)[k];

            if (nextVal !== prevVal) {
                updateData[k] = nextVal;
                sentGenSnapshot[k] = entry?.gen ?? 0;
            }
        }

        // Clear pending before request; re-add on failure
        pendingRef.current = {};

        if (!Object.keys(updateData).length) return;

        inFlightRef.current = true;
        setSaving(true);

        // Fire-and-forget: let the save continue in background even if user exits
        updateProfile(updateData)
            .then((updated) => {
                const updatedProfile = (updated ?? {}) as any as ProfileData;

                // Update lastSynced with what server returned
                lastSyncedRef.current = updatedProfile || {};

                // Clear dirty flags ONLY if:
                // - server matches local
                // - AND there were no newer edits since this request was sent (generation check)
                const latestDraft = draftRef.current || {};
                for (const k of Object.keys(updateData)) {
                    const localVal = (latestDraft as any)[k];
                    const serverVal = (updatedProfile as any)[k];

                    const currentGen = fieldGenRef.current[k] ?? 0;
                    const sentGen = sentGenSnapshot[k] ?? 0;

                    if (currentGen !== sentGen) continue;

                    if (localVal === serverVal) {
                        dirtyRef.current.delete(k);
                    }
                }

                // IMPORTANT:
                // Do NOT merge server profile into draft while user is still editing other fields.
                // Only merge if we are fully idle now.
                applyAvatarFromProfile(updatedProfile || {});

                // Check if user started new edits while we were away (ignoring the just-completed flight)
                const hasNewEdits = dirtyRef.current.size > 0 || Object.keys(pendingRef.current || {}).length > 0;

                if (!hasNewEdits) {
                    setDraft((prev) => {
                        const next: any = { ...(prev || {}) };
                        for (const k of Object.keys(updatedProfile || {})) {
                            next[k] = (updatedProfile as any)[k];
                        }
                        return next as ProfileData;
                    });
                }

                setError(null);
            })
            .catch((err) => {
                console.error("Failed to update profile (debounced):", err);
                showError("Failed to save profile changes");

                // Re-add the data we attempted to save so it can retry on next change/blur
                const reAdd: PendingMap = {};
                for (const [k, v] of Object.entries(updateData)) {
                    reAdd[k] = { value: v, gen: sentGenSnapshot[k] ?? 0 };
                }

                pendingRef.current = {
                    ...(reAdd as PendingMap),
                    ...(pendingRef.current || {}),
                };

                setError("Failed to save changes");

                if (flushRequestedRef.current) {
                    flushSoon();
                }
            })
            .finally(() => {
                inFlightRef.current = false;
                setSaving(false);
            });
    };

    const scheduleFlush = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        timerRef.current = window.setTimeout(() => {
            flushPending();
        }, debounceMs);
    };

    // flush after the blur tick so the last onChange has definitely landed
    const flushSoon = () => {
        if (timerRef.current) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        window.setTimeout(() => {
            flushPending();
        }, 0);
    };

    // On unmount: flush any pending (best-effort, no await)
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            void flushPending();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const queueField = (
        field: keyof ProfileData,
        value: any,
        shouldSchedule = true
    ) => {
        const k = String(field);

        // mark dirty immediately so any profile rehydrate won't overwrite
        dirtyRef.current.add(k);

        // bump generation for this field
        const nextGen = (fieldGenRef.current[k] ?? 0) + 1;
        fieldGenRef.current[k] = nextGen;

        pendingRef.current = {
            ...(pendingRef.current || {}),
            [k]: { value, gen: nextGen },
        };

        if (shouldSchedule) {
            scheduleFlush();
        }
    };

    const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setDraft((prev) => ({ ...prev, first_name: v }));
        // Don't auto-save on every keystroke (prevents UI flicker/refresh feel)
        queueField("first_name", v, false);
    };

    const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setDraft((prev) => ({ ...prev, last_name: v }));
        queueField("last_name", v, false);
    };

    const handleNicknameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setDraft((prev) => ({ ...prev, nickname: v }));
        queueField("nickname", v, false);
    };

    const handleCompanyChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setDraft((prev) => ({ ...prev, company_name: v }));
        queueField("company_name", v, false);
    };

    const handleTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setDraft((prev) => ({ ...prev, title: v }));
        queueField("title", v, false);
    };

    const handleStateChange = (event: ChangeEvent<HTMLInputElement>) => {
        const v = event.target.value;
        setDraft((prev) => ({ ...prev, state: v }));
        queueField("state", v, false);
    };

    const handleFirstNameBlur = () => void flushSoon();
    const handleLastNameBlur = () => void flushSoon();
    const handleNicknameBlur = () => void flushSoon();
    const handleCompanyBlur = () => void flushSoon();
    const handleTitleBlur = () => void flushSoon();
    const handleStateBlur = () => void flushSoon();

    const handlePhotoUpload = async (file: File) => {
        try {
            setError(null);
            setSaving(true);

            const updated = await uploadProfilePhoto(file);
            const updatedProfile = (updated ?? {}) as any as ProfileData;

            lastSyncedRef.current = updatedProfile || {};
            applyAvatarFromProfile(updatedProfile || {});

            // If not editing, safe to hydrate draft with server truth
            if (!isEditingNow()) {
                setDraft((prev) => {
                    const next: any = { ...(prev || {}) };
                    for (const k of Object.keys(updatedProfile || {})) {
                        next[k] = (updatedProfile as any)[k];
                    }
                    return next as ProfileData;
                });
            }

            showSuccess("Profile photo updated");
        } catch (err) {
            console.error("Failed to upload photo:", err);
            showError("Failed to upload photo");
            setError("Failed to upload photo");
        } finally {
            setSaving(false);
        }
    };

    const handlePhotoClick = () => {
        // hook up your uploader
    };

    const markAvatarUrlBad = (url: string | undefined) => {
        const u = normalizeAvatarUrl(url);
        if (!u) return;

        badAvatarUrlRef.current = u;

        if (displayAvatarUrl === u) {
            const fallback = lastGoodAvatarUrlRef.current;
            if (
                fallback && fallback !== u &&
                fallback !== badAvatarUrlRef.current
            ) {
                setDisplayAvatarUrl(fallback);
            } else {
                setDisplayAvatarUrl(undefined);
            }
        }

        if (lastGoodAvatarUrlRef.current === u) {
            lastGoodAvatarUrlRef.current = undefined;
        }
    };

    return {
        firstName,
        lastName,
        nickname,
        company,
        title,
        state,
        avatarUrl: displayAvatarUrl,
        initials,

        loading: profileLoading || saving,
        error,

        handleFirstNameChange,
        handleFirstNameBlur,
        handleLastNameChange,
        handleLastNameBlur,
        handleTitleChange,
        handleTitleBlur,
        handleNicknameChange,
        handleNicknameBlur,
        handleCompanyChange,
        handleCompanyBlur,
        handleStateChange,
        handleStateBlur,
        handlePhotoUpload,
        handlePhotoClick,

        markAvatarUrlBad,
    };
};
